/**
 * Devices.tsx — the "Access" host view: a three-pane layout (groups · device
 * list · details) for unattended endpoints, with live presence (SPEC §3 GET
 * /devices; architecture §4.5 presence over WS).
 *
 * Presence comes from a WS subscription (Redis-backed broker), merged over the
 * REST snapshot so the `online` dot is real-time without polling Postgres.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useApi } from '@/api/provider';
import { useAuthStore } from '@/auth/store';
import { subscribePresence, type PresenceDelta } from '@/lib/presence';
import { buildLaunchUrl, launchNativeApp } from '@/lib/launch';
import type { Device, SessionMode } from '@/types';

export default function Devices() {
  const api = useApi();
  const navigate = useNavigate();
  const access = useAuthStore((s) => s.access);

  const { data: devices = [], isLoading, error } = useQuery({
    queryKey: ['devices'],
    queryFn: () => api.listDevices(),
  });

  const { data: groups = [] } = useQuery({
    queryKey: ['device-groups'],
    queryFn: () => api.listDeviceGroups(),
  });

  const [presence, setPresence] = useState<Record<string, PresenceDelta>>({});
  const [search, setSearch] = useState('');
  // The device list carries group NAMES (server deviceView.groups is []string),
  // so we filter by group name rather than id.
  const [groupName, setGroupName] = useState<string>('');
  const [presenceUp, setPresenceUp] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // When the native-app launch can't be confirmed (app not installed, or the mint
  // failed), offer a fallback: open the session in the browser instead.
  const [launchFallback, setLaunchFallback] = useState<{ deviceId: string; mode: SessionMode } | null>(null);

  useEffect(() => {
    if (!access) return;
    const dispose = subscribePresence({
      token: access,
      onStatus: setPresenceUp,
      onSnapshot: (list) =>
        setPresence(Object.fromEntries(list.map((d) => [d.device_id, d]))),
      onDelta: (d) => setPresence((p) => ({ ...p, [d.device_id]: d })),
    });
    return dispose;
  }, [access]);

  // All devices with live presence merged in (unfiltered — used for counts + details).
  const merged = useMemo<Device[]>(
    () =>
      devices.map((d) => {
        const live = presence[d.id];
        return live ? { ...d, online: live.online, last_seen_at: live.last_seen_at } : d;
      }),
    [devices, presence],
  );

  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    return merged
      .filter((d) => {
        if (groupName && !d.groups.includes(groupName)) return false;
        if (!term) return true;
        return (
          d.name.toLowerCase().includes(term) ||
          d.hostname.toLowerCase().includes(term) ||
          d.os.toLowerCase().includes(term)
        );
      })
      .sort((a, b) => Number(b.online) - Number(a.online) || a.name.localeCompare(b.name));
  }, [merged, search, groupName]);

  const selected = merged.find((d) => d.id === selectedId) ?? null;

  function inGroup(name: string): Device[] {
    return name ? merged.filter((d) => d.groups.includes(name)) : merged;
  }
  const onlineOf = (list: Device[]) => list.filter((d) => d.online).length;

  // "Control"/"View only" launch the NATIVE Helm host app (helm:// deep link) the
  // way ScreenConnect's "Join" launches its native client. We mint a one-time launch
  // token, invoke the protocol, and — if the app doesn't take over within a couple
  // seconds (not installed) or the mint fails — fall back to opening the session in
  // the browser (the /session route still works).
  async function connect(d: Device, mode: SessionMode) {
    if (!d.online) return;
    try {
      const lt = await api.createLaunchToken(d.id, mode);
      launchNativeApp(buildLaunchUrl(lt), {
        onFail: () => setLaunchFallback({ deviceId: d.id, mode }),
      });
    } catch {
      // Mint failed (offline race, RBAC, network): let the user open in the browser.
      setLaunchFallback({ deviceId: d.id, mode });
    }
  }

  return (
    <div className="flex h-[calc(100vh-3.5rem)]">
      {/* ---- Groups pane ---- */}
      <aside className="hidden w-56 shrink-0 flex-col border-r border-helm-border bg-helm-panel md:flex">
        <div className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wider text-helm-muted/70">Groups</div>
        <div className="flex-1 space-y-0.5 overflow-auto px-2 pb-3">
          <GroupItem label="All devices" active={groupName === ''} online={onlineOf(merged)} total={merged.length} onClick={() => setGroupName('')} />
          {groups.map((g) => {
            const list = inGroup(g.name);
            return (
              <GroupItem
                key={g.id}
                label={g.name}
                active={groupName === g.name}
                online={onlineOf(list)}
                total={list.length}
                onClick={() => setGroupName(g.name)}
              />
            );
          })}
        </div>
      </aside>

      {/* ---- List pane ---- */}
      <section className="flex min-w-0 flex-1 flex-col">
        <div className="flex items-center gap-3 border-b border-helm-border px-5 py-3">
          <h1 className="text-base font-bold text-helm-text">Access</h1>
          <span
            className={`rh-badge ${presenceUp ? 'bg-helm-good/15 text-helm-good' : 'bg-helm-elevated text-helm-muted'}`}
            title="Live presence stream"
          >
            <span className={`h-1.5 w-1.5 rounded-full ${presenceUp ? 'bg-helm-good' : 'bg-helm-muted'}`} />
            {presenceUp ? 'Live' : 'Offline'}
          </span>
          <span className="text-sm text-helm-muted">{onlineOf(rows)} online · {rows.length} shown</span>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name / hostname / OS…"
            className="rh-input ml-auto w-64"
          />
          <button
            type="button"
            onClick={() => navigate('/admin/devices')}
            className="rh-btn-primary flex items-center gap-2 whitespace-nowrap"
            title="Build and download an access installer"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M12 3v12M7 10l5 5 5-5M5 21h14" />
            </svg>
            Deploy access
          </button>
        </div>

        <div className="flex-1 overflow-auto">
          <table className="rh-table">
            <thead>
              <tr>
                <th>Status</th>
                <th>Name</th>
                <th>Hostname</th>
                <th>OS</th>
                <th>Last seen</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((d) => (
                <tr
                  key={d.id}
                  onClick={() => setSelectedId(d.id)}
                  className={`cursor-pointer ${selectedId === d.id ? 'bg-helm-accent/10' : ''}`}
                >
                  <td>
                    <span className={`rh-badge ${d.online ? 'bg-helm-good/15 text-helm-good' : 'bg-helm-elevated text-helm-muted'}`}>
                      <span className={`h-1.5 w-1.5 rounded-full ${d.online ? 'bg-helm-good' : 'bg-helm-muted'}`} />
                      {d.online ? 'Online' : 'Offline'}
                    </span>
                    {d.ephemeral && <span className="rh-badge ml-1.5 bg-helm-warn/15 text-helm-warn">attended</span>}
                  </td>
                  <td className="font-medium text-helm-text">
                    <span className="flex items-center gap-2">
                      <span className="shrink-0 text-helm-muted">{osIcon(d.os)}</span>
                      <span className="truncate">{d.name || '—'}</span>
                    </span>
                  </td>
                  <td className="text-helm-muted">{d.hostname || '—'}</td>
                  <td className="text-helm-muted">{d.os} {d.os_version}</td>
                  <td className="text-helm-muted">{fmt(d.last_seen_at)}</td>
                </tr>
              ))}
              {isLoading && (
                <tr><td colSpan={5} className="py-10 text-center text-helm-muted">Loading devices…</td></tr>
              )}
              {error && !isLoading && (
                <tr><td colSpan={5} className="py-10 text-center text-helm-bad">Failed to load devices.</td></tr>
              )}
              {rows.length === 0 && !isLoading && !error && (
                <tr><td colSpan={5} className="py-12 text-center text-helm-muted">No devices match.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* ---- Details pane ---- */}
      <aside className="hidden w-80 shrink-0 overflow-auto border-l border-helm-border bg-helm-panel lg:block">
        {selected ? (
          <div className="p-5">
            <div className="mb-4 flex items-start gap-3">
              <div className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-helm-elevated text-helm-muted">
                {osIcon(selected.os, 22)}
              </div>
              <div className="min-w-0">
                <div className="truncate font-semibold text-helm-text">{selected.name || selected.hostname || 'Unnamed device'}</div>
                <span className={`rh-badge mt-1 ${selected.online ? 'bg-helm-good/15 text-helm-good' : 'bg-helm-elevated text-helm-muted'}`}>
                  <span className={`h-1.5 w-1.5 rounded-full ${selected.online ? 'bg-helm-good' : 'bg-helm-muted'}`} />
                  {selected.online ? 'Online' : 'Offline'}
                </span>
              </div>
            </div>

            <div className="mb-5 grid grid-cols-2 gap-2.5">
              <button type="button" disabled={!selected.online} onClick={() => void connect(selected, 'control')} className="rh-btn-primary">
                Control
              </button>
              <button type="button" disabled={!selected.online} onClick={() => void connect(selected, 'view')} className="rh-btn-ghost">
                View only
              </button>
            </div>

            {launchFallback?.deviceId === selected.id && (
              <div className="mb-5 rounded-lg border border-helm-border bg-helm-elevated/40 p-3 text-sm">
                <div className="mb-1 font-medium text-helm-text">Opening the Helm app…</div>
                <p className="mb-3 text-helm-muted">
                  If the Helm app didn’t open, install it — or open this session in your browser instead.
                </p>
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => navigate(`/session/${launchFallback.deviceId}?mode=${launchFallback.mode}`)}
                    className="rh-btn-primary"
                  >
                    Open in browser
                  </button>
                  {/* Interim: /admin/devices currently builds the AGENT installer, not the
                      technician helmhost client — a dedicated helmhost download is a follow-up. */}
                  <button type="button" onClick={() => navigate('/admin/devices')} className="rh-btn-ghost">
                    Get the Helm app
                  </button>
                  <button
                    type="button"
                    onClick={() => setLaunchFallback(null)}
                    className="ml-auto text-helm-muted hover:text-helm-text"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            )}

            <dl className="space-y-3 text-sm">
              <Detail label="Hostname" value={selected.hostname || '—'} />
              <Detail label="Operating system" value={`${selected.os} ${selected.os_version}`.trim() || '—'} />
              <Detail label="Groups" value={selected.groups.length ? selected.groups.join(', ') : 'Ungrouped'} />
              <Detail label="Last seen" value={fmt(selected.last_seen_at)} />
              <Detail label="Type" value={selected.ephemeral ? 'Attended (one-time)' : 'Unattended'} />
              <Detail label="Device ID" value={selected.id} mono />
            </dl>
          </div>
        ) : (
          <div className="flex h-full flex-col items-center justify-center gap-3 p-8 text-center text-helm-muted">
            <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" className="opacity-50" aria-hidden="true">
              <rect x="2" y="3" width="20" height="14" rx="2" /><path d="M8 21h8M12 17v4" />
            </svg>
            <p className="text-sm">Select a device to see its details and connect.</p>
          </div>
        )}
      </aside>
    </div>
  );
}

function GroupItem({ label, active, online, total, onClick }: { label: string; active: boolean; online: number; total: number; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm transition ${
        active ? 'bg-helm-accent/15 font-semibold text-helm-text' : 'text-helm-muted hover:bg-helm-elevated hover:text-helm-text'
      }`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${online > 0 ? 'bg-helm-good' : 'bg-helm-border'}`} />
      <span className="min-w-0 flex-1 truncate text-left">{label}</span>
      <span className="text-xs text-helm-muted">{online}/{total}</span>
    </button>
  );
}

function Detail({ label, value, mono = false }: { label: string; value: string; mono?: boolean }) {
  return (
    <div>
      <dt className="text-[11px] font-medium uppercase tracking-wide text-helm-muted/70">{label}</dt>
      <dd className={`mt-0.5 break-words text-helm-text ${mono ? 'font-mono text-xs' : ''}`}>{value}</dd>
    </div>
  );
}

/**
 * osIcon — a small inline OS/type glyph derived from the device.os string.
 * Windows / Apple-mac / Linux, falling back to a generic monitor. No external
 * assets; stroke uses currentColor so it inherits the surrounding text color.
 */
function osIcon(os: string, size = 16) {
  const o = (os || '').toLowerCase();
  const common = {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none' as const,
    stroke: 'currentColor',
    strokeWidth: 1.6,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': true,
  };
  if (o.includes('win')) {
    return (
      <svg {...common}><title>Windows</title><path d="M3 5.5 10.5 4.4V11H3zM10.5 13v6.6L3 18.5V13zM12.5 4.1 21 3v8h-8.5zM21 13v8l-8.5-1.1V13z" /></svg>
    );
  }
  if (o.includes('mac') || o.includes('apple') || o.includes('darwin') || o.includes('ios') || o.includes('osx')) {
    return (
      <svg {...common}><title>macOS</title><path d="M16 13c0 3 2.2 4 2.2 4-.3 1-1 2-1.8 2.8-.8.8-1.6 1.5-2.7 1.5-1 0-1.4-.6-2.7-.6s-1.7.6-2.7.6c-1 0-1.9-.8-2.7-1.6C3 18.7 2 15.5 3.3 13c.7-1.3 2-2.1 3.4-2.1 1 0 2 .7 2.7.7.6 0 1.8-.8 3-.7 1.3.1 2.5.6 3.3 1.7-2.9 1.7-2.4 5.6.3 6.4" /><path d="M14.5 5.5c.6-.8 1-1.9.9-3-1 .1-2.1.7-2.8 1.5-.6.7-1.1 1.8-.9 2.9 1.1.1 2.2-.6 2.8-1.4" /></svg>
    );
  }
  if (o.includes('linux') || o.includes('ubuntu') || o.includes('debian') || o.includes('bsd')) {
    return (
      <svg {...common}><title>Linux</title><path d="M9.5 3.5c-.8.6-1.2 1.7-1.2 3 0 1.6-.7 2.6-1.6 4-.9 1.4-1.7 2.7-1.7 4.6 0 1.5.6 2.6 1.4 3.5M14.5 3.5c.8.6 1.2 1.7 1.2 3 0 1.6.7 2.6 1.6 4 .9 1.4 1.7 2.7 1.7 4.6 0 1.5-.6 2.6-1.4 3.5" /><path d="M9 8.5h.01M15 8.5h.01M10 12c1.3 1 2.7 1 4 0M7.7 18c-.7.7-2 .8-2.7 1.4M16.3 18c.7.7 2 .8 2.7 1.4" /></svg>
    );
  }
  return (
    <svg {...common}><title>Device</title><rect x="2" y="3" width="20" height="14" rx="2" /><path d="M8 21h8M12 17v4" /></svg>
  );
}

function fmt(iso: string | null): string {
  if (!iso) return 'never';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
}
